import { DayOfWeek } from '@pimm/services/lib/sms-tenants/types';
import { DailyHoursShape, EditValues, DAYS } from './types';

export const PHONE_REGEX = /^[+\d\s\-().]*$/;

// Strip seconds from API "HH:MM:SS" → "HH:MM". Null/empty → '-'.
export const parseApiTime = (s?: string | null): string => {
  if (!s) return '-';
  return s.slice(0, 5);
};

// Format "HH:MM" to 12-hour display string. '-' passthrough.
export const formatTime = (timeStr?: string | null): string => {
  if (!timeStr || timeStr === '-') return '-';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
};

export const formatLastModified = (iso?: string, template?: string): string => {
  if (!iso || !template) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return template.replace('{{date}}', dateStr);
};

// Time math helpers
export const hhmmToMinutes = (hhmm?: string | null): number | null => {
  if (!hhmm || hhmm === '-' || hhmm === '') return null;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

export const minutesToHhmm = (mins: number): string => {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// Maps raw store hours to the staged edit shape used in edit mode.
export const toEditValues = (storeHours: DailyHoursShape[]): EditValues =>
  storeHours.map(h => {
    const bfEndRaw = h.breakfastEnd === '-' ? '' : h.breakfastEnd || '';
    return {
      diningRmOpen: h.diningRmOpen === '-' ? '' : h.diningRmOpen || '',
      diningRmClose: h.diningRmClose === '-' ? '' : h.diningRmClose || '',
      driveThruOpen: h.driveThruOpen === '-' ? '' : h.driveThruOpen || '',
      driveThruClose: h.driveThruClose === '-' ? '' : h.driveThruClose || '',
      breakfastStart: h.breakfastStart === '-' ? '' : h.breakfastStart || '',
      breakfastEnd: h.servesBreakfast && !bfEndRaw ? (h.lunchStart === '-' ? '' : h.lunchStart || '') : bfEndRaw,
      lunchStart: h.lunchStart === '-' ? '' : h.lunchStart || '',
      lunchEnd: h.lunchEnd === '-' ? '' : h.lunchEnd || '',
      dinnerStart: h.dinnerStart === '-' ? '' : h.dinnerStart || '',
      dinnerEnd: h.dinnerEnd === '-' ? '' : h.dinnerEnd || '',
    };
  });

// Returns Set of "rowKey-dayIdx" strings for cells that differ from original.
export const computeDirtySet = (editValues: EditValues | null, originalValues: EditValues | null): Set<string> => {
  const dirty = new Set<string>();
  if (!editValues || !originalValues) return dirty;
  const fieldToRowKey: Record<string, string> = {
    diningRmOpen: 'diningRoom',
    diningRmClose: 'diningRoom',
    driveThruOpen: 'driveThru',
    driveThruClose: 'driveThru',
    breakfastStart: 'breakfast',
    breakfastEnd: 'breakfast',
    lunchStart: 'lunch',
    lunchEnd: 'lunch',
    dinnerStart: 'dinner',
    dinnerEnd: 'dinner',
  };
  editValues.forEach((day, dayIdx) => {
    (Object.keys(day) as (keyof typeof day)[]).forEach(field => {
      if (day[field] !== originalValues[dayIdx][field]) {
        const rowKey = fieldToRowKey[field];
        if (rowKey) dirty.add(`${rowKey}-${dayIdx}`);
      }
    });
  });
  return dirty;
};

// Earliest valid breakfast/lunch start for a day — min of active channel opens.
export const getMinBreakfastStart = (dayIdx: number, storeHours: DailyHoursShape[], editValues: EditValues | null): string | null => {
  const sh = storeHours[dayIdx];
  const ev = editValues?.[dayIdx];
  if (!sh || !ev) return null;
  const times: number[] = [];
  if (sh.hasDiningRoom && ev.diningRmOpen && ev.diningRmOpen !== '') times.push(hhmmToMinutes(ev.diningRmOpen) ?? Infinity);
  if (sh.hasPickupWindow && ev.driveThruOpen && ev.driveThruOpen !== '') times.push(hhmmToMinutes(ev.driveThruOpen) ?? Infinity);
  const validTimes = times.filter(isFinite);
  if (validTimes.length === 0) return null;
  return minutesToHhmm(Math.min(...validTimes));
};

// Most-restrictive (latest) min across a set of selected days.
export const getConstraintForDays = (
  dayIndices: number[],
  storeHours: DailyHoursShape[],
  editValues: EditValues | null,
): { minTime: string | null; constrainingDays: number[] } => {
  let maxMins = -1;
  const perDay: Record<number, number> = {};
  dayIndices.forEach(i => {
    const min = getMinBreakfastStart(i, storeHours, editValues);
    if (min !== null) {
      perDay[i] = hhmmToMinutes(min) ?? 0;
      if (perDay[i] > maxMins) maxMins = perDay[i];
    }
  });
  if (maxMins < 0) return { minTime: null, constrainingDays: [] };
  const constrainingDays = Object.entries(perDay)
    .filter(([, v]) => v === maxMins)
    .map(([k]) => parseInt(k));
  return { minTime: minutesToHhmm(maxMins), constrainingDays };
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Cross-row overlap validation for breakfast/lunch/dinner.
export const getOverlapError = (
  t: Translate,
  rowKey: string,
  openVal: string,
  closeVal: string,
  dayIdx: number,
  editValues: EditValues | null,
  storeHours?: DailyHoursShape[],
): string | null => {
  const ev = editValues?.[dayIdx];
  if (!ev) return null;
  const m = hhmmToMinutes;
  switch (rowKey) {
    case 'diningRoom':
    case 'driveThru':
      return null;
    case 'breakfast':
      if (openVal && closeVal && (m(openVal) ?? 0) > (m(closeVal) ?? 0)) return t('store_settings:validation_start_after_end');
      if (closeVal && ev.lunchStart && (m(closeVal) ?? 0) > (m(ev.lunchStart) ?? 0))
        return t('store_settings:validation_breakfast_end_after_lunch_start', { time: formatTime(ev.lunchStart) });
      return null;
    case 'lunch': {
      const servesBreakfast = storeHours ? !!storeHours[dayIdx]?.servesBreakfast : true;
      if (servesBreakfast && openVal && ev.breakfastEnd && (m(ev.breakfastEnd) ?? 0) > (m(openVal) ?? 0))
        return t('store_settings:validation_lunch_start_before_breakfast_end', { time: formatTime(ev.breakfastEnd) });
      if (openVal && closeVal && (m(openVal) ?? 0) > (m(closeVal) ?? 0)) return t('store_settings:validation_start_after_end');
      if (closeVal && ev.dinnerStart && (m(closeVal) ?? 0) > (m(ev.dinnerStart) ?? 0))
        return t('store_settings:validation_lunch_end_after_dinner_start', { time: formatTime(ev.dinnerStart) });
      return null;
    }
    case 'dinner':
      if (openVal && ev.lunchEnd && (m(ev.lunchEnd) ?? 0) > (m(openVal) ?? 0))
        return t('store_settings:validation_dinner_start_before_lunch_end', { time: formatTime(ev.lunchEnd) });
      if (openVal && closeVal && (m(openVal) ?? 0) > (m(closeVal) ?? 0)) return t('store_settings:validation_start_after_end');
      return null;
    default:
      return null;
  }
};

// Payload builder — server expects TimeSpan "00.HH:MM:SS" format and all 7 days.
const toTimeSpan = (hhmm?: string | null): string | undefined => {
  if (!hhmm || hhmm === '-' || hhmm === '') return undefined;
  return `00.${hhmm}:00`;
};

const timePair = (start?: string | null, end?: string | null): [string | undefined, string | undefined] => [
  toTimeSpan(start),
  toTimeSpan(end),
];

export type StoreHoursPayload = ReturnType<typeof buildPayload>;

export const buildPayload = (editValues: EditValues, storeHours: DailyHoursShape[]) => ({
  dailyHours: editValues.map((ev, i) => {
    const sh = storeHours[i];
    const [diningOpen, diningClose] = sh?.hasDiningRoom ? timePair(ev.diningRmOpen, ev.diningRmClose) : [null, null];
    const [driveOpen, driveClose] = sh?.hasPickupWindow ? timePair(ev.driveThruOpen, ev.driveThruClose) : [null, null];
    const [bfStart, bfEnd] = sh?.servesBreakfast ? timePair(ev.breakfastStart, ev.breakfastEnd) : [null, null];
    const [lunchStart, lunchEnd] = !sh?.isClosed ? timePair(ev.lunchStart, ev.lunchEnd) : [null, null];
    const [dinnerStart, dinnerEnd] = !sh?.isClosed ? timePair(ev.dinnerStart, ev.dinnerEnd) : [null, null];
    return {
      dayOfWeek: (i === 6 ? 0 : i + 1) as DayOfWeek, // convert Mon=0..Sun=6 back to Sun=0..Sat=6 for API
      diningRmOpen: diningOpen,
      diningRmClose: diningClose,
      driveThruOpen: driveOpen,
      driveThruClose: driveClose,
      breakfastStart: bfStart,
      breakfastEnd: bfEnd,
      lunchStart,
      lunchEnd,
      dinnerStart,
      dinnerEnd,
    };
  }),
});

// Remap API dayOfWeek (0=Sun…6=Sat) to internal order (Mon=0…Sun=6).
export const parseApiStoreHours = (apiDays: any[]): DailyHoursShape[] => {
  const result: DailyHoursShape[] = new Array(7);
  apiDays.forEach(d => {
    const protoIdx = d.dayOfWeek === 0 ? 6 : d.dayOfWeek - 1;
    result[protoIdx] = mapApiDay(d, DAYS[protoIdx]);
  });
  return result;
};

const mapApiDay = (apiDay: any, dayName: string): DailyHoursShape => {
  if (apiDay.isClosed) {
    return {
      dayOfWeek: dayName,
      isClosed: true,
      open24Hrs: false,
      servesBreakfast: false,
      hasDiningRoom: false,
      hasPickupWindow: false,
      diningRmOpen: '-',
      diningRmClose: '-',
      driveThruOpen: '-',
      driveThruClose: '-',
      breakfastStart: '-',
      breakfastEnd: '-',
      lunchStart: '-',
      lunchEnd: '-',
      dinnerStart: '-',
      dinnerEnd: '-',
      storeOpen: '-',
      storeClose: '-',
    };
  }
  return {
    dayOfWeek: dayName,
    isClosed: false,
    open24Hrs: !!apiDay.open24Hrs,
    servesBreakfast: !!apiDay.servesBreakfast,
    hasDiningRoom: !!apiDay.hasDiningRoom,
    hasPickupWindow: !!apiDay.hasPickupWindow,
    diningRmOpen: apiDay.hasDiningRoom ? parseApiTime(apiDay.diningRmOpen) : '-',
    diningRmClose: apiDay.hasDiningRoom ? parseApiTime(apiDay.diningRmClose) : '-',
    driveThruOpen: apiDay.hasPickupWindow ? parseApiTime(apiDay.driveThruOpen) : '-',
    driveThruClose: apiDay.hasPickupWindow ? parseApiTime(apiDay.driveThruClose) : '-',
    breakfastStart: apiDay.servesBreakfast ? parseApiTime(apiDay.breakfastStart) : '-',
    breakfastEnd: apiDay.servesBreakfast ? parseApiTime(apiDay.breakfastEnd) : '-',
    lunchStart: parseApiTime(apiDay.lunchStart),
    lunchEnd: parseApiTime(apiDay.lunchEnd),
    dinnerStart: parseApiTime(apiDay.dinnerStart),
    dinnerEnd: parseApiTime(apiDay.dinnerEnd),
    storeOpen: parseApiTime(apiDay.storeOpen),
    storeClose: parseApiTime(apiDay.storeClose),
  };
};

// 24h ↔ 12h helpers (used by TimeSpinner)
export const hhmm24To12 = (hhmm?: string | null): { hour: number; minute: number; ampm: 'AM' | 'PM' } => {
  if (!hhmm) return { hour: 12, minute: 0, ampm: 'AM' };
  const [h, m] = hhmm.split(':').map(Number);
  return { hour: h % 12 || 12, minute: m, ampm: h >= 12 ? 'PM' : 'AM' };
};

export const to24 = (hour: number, minute: number, ampm: 'AM' | 'PM'): string => {
  let h = hour % 12;
  if (ampm === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

// ─── date input mask (MM / DD / YYYY) — Hire Date ───────────────────────────
// Auto-formatting for masked date inputs: the manager types digits only and
// the " / " separators are inserted as each segment fills, so there's no
// manual slash-typing and no free-form date parsing to get wrong.

// Reformat raw input to "MM / DD / YYYY": strip non-digits, cap at 8, join the
// month/day/year segments with " / " as each one starts. Append-only typing
// keeps the caret at the end; backspacing a separator just removes the digit
// before it on the next keystroke, which is the expected mask behavior.
export const maskDate = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  const parts: string[] = [digits.slice(0, 2)];
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.join(' / ');
};

// Parse a fully-typed "MM / DD / YYYY" — returns null unless all 8 digits are
// present AND form a real calendar date (rejects 02 / 30, month 13, etc. via
// the round-trip check). Year floor 1900 guards against a mistyped 2-digit year.
export const parseMaskedDate = (v: string): { mm: number; dd: number; yyyy: number } | null => {
  const digits = v.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  const mm = Number(digits.slice(0, 2));
  const dd = Number(digits.slice(2, 4));
  const yyyy = Number(digits.slice(4, 8));
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || yyyy < 1900) return null;
  const dt = new Date(yyyy, mm - 1, dd);
  if (dt.getFullYear() !== yyyy || dt.getMonth() !== mm - 1 || dt.getDate() !== dd) return null;
  return { mm, dd, yyyy };
};

// Empty is valid (dates are optional); a partial/invalid date is not.
export const isValidOrEmptyDate = (v: string) => v.trim() === '' || parseMaskedDate(v) !== null;

// True when v is a complete, valid date that falls AFTER today — a birthday
// or hire date can't be in the future. Date-level compare (today itself is
// allowed). Empty / partial / invalid returns false so the separate "valid
// date" check owns those cases and the messages don't stack.
export const isFutureDate = (v: string): boolean => {
  const p = parseMaskedDate(v);
  if (!p) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(p.yyyy, p.mm - 1, p.dd).getTime() > today.getTime();
};

// "MM / DD / YYYY" -> "YYYY-MM-DD" for the API's DateTime field. undefined when
// empty or incomplete (caller omits the field).
export const toIsoDate = (v: string): string | undefined => {
  const p = parseMaskedDate(v);
  if (!p) return undefined;
  return `${p.yyyy}-${String(p.mm).padStart(2, '0')}-${String(p.dd).padStart(2, '0')}`;
};

// ISO datetime (e.g. "2021-06-01T00:00:00Z", also handles bare "YYYY-MM-DD")
// -> "MM / DD / YYYY", for populating a masked Hire Date input from an API
// value. Uses UTC calendar fields, NOT local getters — same reasoning as
// fromIsoMonthDay below: the API returns hireDate as a UTC-suffixed
// datetime, and local getters would roll the displayed date back a day in
// any negative-UTC-offset timezone. Empty/unparseable -> ''.
export const fromIsoDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  return `${mm} / ${dd} / ${yyyy}`;
};

// ─── date input mask (MM / DD, no year) — Birthday ──────────────────────────
// Birthday is intentionally year-less across the app (no year is collected or
// displayed) — the API's birthDate field is still a full ISO datetime, so a
// placeholder/preserved year is threaded through separately by the caller
// (toIsoBirthDate) rather than being part of what the manager types.

// Reformat raw input to "MM / DD": strip non-digits, cap at 4, join the
// month/day segments with " / " as each one starts.
export const maskMonthDay = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  const parts: string[] = [digits.slice(0, 2)];
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  return parts.join(' / ');
};

// Parse a fully-typed "MM / DD" — returns null unless all 4 digits are present
// AND form a real month/day (no year to validate against, so Feb is allowed up
// to 29 unconditionally — a leap-day birthday is valid every year it recurs).
const DAYS_IN_MONTH_LEAP = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const parseMaskedMonthDay = (v: string): { mm: number; dd: number } | null => {
  const digits = v.replace(/\D/g, '');
  if (digits.length !== 4) return null;
  const mm = Number(digits.slice(0, 2));
  const dd = Number(digits.slice(2, 4));
  if (mm < 1 || mm > 12 || dd < 1 || dd > DAYS_IN_MONTH_LEAP[mm - 1]) return null;
  return { mm, dd };
};

// Empty is valid (birthday is optional); a partial/invalid month-day is not.
export const isValidMonthDayOrEmpty = (v: string) => v.trim() === '' || parseMaskedMonthDay(v) !== null;

// "MM / DD" -> full ISO "YYYY-MM-DD" for the API's DateTime field, using
// `year` if given (preserve whatever real year is already on the record when
// editing) or a fixed placeholder when there's no existing value to preserve
// (a brand-new employee) — 1900 matches the floor already used as the
// "no real year provided" boundary in parseMaskedDate above. undefined when
// empty or incomplete (caller omits the field).
export const toIsoBirthDate = (v: string, year?: number): string | undefined => {
  const p = parseMaskedMonthDay(v);
  if (!p) return undefined;
  const yyyy = year ?? 1900;
  return `${yyyy}-${String(p.mm).padStart(2, '0')}-${String(p.dd).padStart(2, '0')}`;
};

// ISO datetime (e.g. "1988-03-12T00:00:00Z", also handles bare "YYYY-MM-DD")
// -> "MM / DD", for populating the masked birthday input from an API value.
// Uses UTC calendar fields, NOT local getters — the API returns birthDate as
// a UTC-suffixed datetime, and local getters would roll the displayed date
// back a day in any negative-UTC-offset timezone (all of the US).
// Empty/unparseable -> ''.
export const fromIsoMonthDay = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${mm} / ${dd}`;
};

// Extracts the real stored year from an existing ISO birthDate, so an edit
// can preserve it even though the UI never shows/collects it. undefined when
// empty/unparseable (a brand-new record — caller falls back to the
// toIsoBirthDate placeholder instead).
export const getIsoYear = (iso?: string | null): number | undefined => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.getUTCFullYear();
};
